import ImpersonationFrameDialog from '@/components/common/ImpersonationFrameDialog';
import { useLocalization } from '@/hooks/useLocalization';
import { registerActiveFrameMarker } from '@/utils/impersonationFrame';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type AccessSession = {
  investorId: number;
  src: string;
  marker: string;
};

type UseAccessInvestorOptions = {
  onClose?: () => void;
};

export type UseAccessInvestorResult = {
  startAccess: (investorId: number) => void;
  resetAccess: () => void;
  accessingInvestorId: number | null;
  frame: React.ReactNode;
  error: string | null;
  clearError: () => void;
};

type ImpersonationMessage = {
  type?: string;
  frameMarker?: string | null;
};

const createFrameMarker = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${String(Date.now())}-${Math.random().toString(36).slice(2, 8)}`;

const buildAccessSession = (investorId: number): AccessSession => {
  const url = new URL(import.meta.env.BASE_URL || '/', window.location.href);
  const marker = createFrameMarker();
  url.searchParams.set('impersonationAction', 'AccessInvestor');
  url.searchParams.set('investorId', String(investorId));
  url.searchParams.set('impersonationFrame', marker);
  return { investorId, src: url.toString(), marker };
};

const rootDomain = (hostname: string): string => {
  const parts = hostname.split('.');
  return parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
};

// The frame can run on a sibling subdomain (v3-frontend.wealthaccess.com ->
// beta.wealthaccess.com), so a shared root domain is trusted as well.
const isTrustedOrigin = (origin: string): boolean => {
  if (origin === window.location.origin) return true;
  try {
    return (
      rootDomain(new URL(origin).hostname) ===
      rootDomain(window.location.hostname)
    );
  } catch {
    return false;
  }
};

export const useAccessInvestor = (
  options?: UseAccessInvestorOptions,
): UseAccessInvestorResult => {
  const { t } = useLocalization();
  const onClose = options?.onClose;

  const [session, setSession] = useState<AccessSession | null>(null);
  const sessionRef = useRef<AccessSession | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateSession = useCallback((next: AccessSession | null) => {
    sessionRef.current = next;
    setSession(next);
  }, []);

  const startAccess = useCallback(
    (investorId: number) => {
      if (sessionRef.current) return;
      updateSession(buildAccessSession(investorId));
      setIsLoading(true);
    },
    [updateSession],
  );

  const resetAccess = useCallback(() => {
    updateSession(null);
    setIsLoading(false);
    setError(null);
  }, [updateSession]);

  const closeAccess = useCallback(() => {
    updateSession(null);
    setIsLoading(false);
    onClose?.();
  }, [onClose, updateSession]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const marker = session?.marker ?? null;

  useEffect(() => {
    if (!marker) return undefined;
    return registerActiveFrameMarker(marker);
  }, [marker]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedOrigin(event.origin)) return;
      if (!event.data || typeof event.data !== 'object') return;

      const message = event.data as ImpersonationMessage;
      if (!marker || message.frameMarker !== marker) return;

      if (message.type === 'impersonation-exit') {
        closeAccess();
        return;
      }

      if (message.type === 'impersonation-action-error') {
        setError(
          t(
            'accessInvestorError',
            "We couldn't access this investor right now.",
          ),
        );
        closeAccess();
        return;
      }

      if (message.type === 'impersonation-action-success') {
        setIsLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [closeAccess, marker, t]);

  const frame = useMemo(
    () =>
      session ? (
        <ImpersonationFrameDialog
          open
          src={session.src}
          onLoaded={() => {
            setIsLoading(false);
          }}
          isLoading={isLoading}
        />
      ) : null,
    [session, isLoading],
  );

  return {
    startAccess,
    resetAccess,
    accessingInvestorId: session?.investorId ?? null,
    frame,
    error,
    clearError,
  };
};

export default useAccessInvestor;
